import Classnames from "classnames";
import { useEffect, useState } from "react";

import Link from "@router/link";
import Checkbox from "@shared/checkbox";
import Chip from "@shared/chip";
import Notification from "@shared/notification";
import { useTermsContent } from "@utils/api/queries";
import i18n from "@utils/i18n";
import { sanitizeHtml } from "@utils/sanitize-html";
import { getServiceName, Terms } from "@utils/types";

import style from "./consent-documents.styl";
import Modal from "./modal";

const PRIVACY_POLICY_ID = "privacyPolicy";

type ConsentDocumentsProps = {
  documents: Terms[];
  title: string;
  agreementTitle: string;
  showPrivacyPolicy?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  onAgreedChange?: (agreed: boolean) => void;
};

const ConsentDocuments = ({
  documents = [],
  title,
  agreementTitle,
  showPrivacyPolicy,
  isLoading,
  isError,
  onAgreedChange,
}: ConsentDocumentsProps) => {
  const [agreedDocumentIds, setAgreedDocumentIds] = useState<Terms["id"][]>([]);

  const [agreed, setAgreed] = useState(false);

  const [selectedDocumentId, setSelectedDocumentId] = useState<
    Terms["id"] | null
  >(null);

  const selectedDocument = documents.find(
    (document) => document.id === selectedDocumentId,
  );

  const {
    data: selectedDocumentContent,
    isError: isDocumentContentError,
    isLoading: isLoadingDocumentContent,
  } = useTermsContent(selectedDocument?.id, selectedDocument?.contentLink);

  const allDocumentsAgreed =
    documents.every((document) => agreedDocumentIds.includes(document.id)) &&
    (!showPrivacyPolicy || agreedDocumentIds.includes(PRIVACY_POLICY_ID));

  const hasDocuments = documents.length > 0;
  const documentsUnavailable = !isLoading && (isError || !hasDocuments);
  const canAgree = !isLoading && !documentsUnavailable;

  const markDocumentAsAgreed = (documentId: Terms["id"]) => {
    setAgreedDocumentIds((ids) =>
      ids.includes(documentId) ? ids : [...ids, documentId],
    );
  };

  const onDocumentAgree = () => {
    if (!selectedDocument) {
      return;
    }
    markDocumentAsAgreed(selectedDocument.id);
    setSelectedDocumentId(null);
  };

  const onAgreementChange = (value: boolean) => {
    setAgreed(value);
  };

  const renderConsentModal = () => (
    <Modal
      open={Boolean(selectedDocument)}
      close={() => setSelectedDocumentId(null)}
      title={i18n.t("common:terms", {
        service: getServiceName(selectedDocument?.service),
      })}
      action={onDocumentAgree}
      actionDisabled={isDocumentContentError}
      actionLabel={i18n.t("common:agree")}
      pending={isLoadingDocumentContent}
      size="xLarge"
      data-testid="consent-modal"
    >
      <div className={style.modalContent}>
        <Notification
          hideCloseButton
          open={isDocumentContentError}
          type="critical"
          text={i18n.t("common:termsDocumentFetchError")}
        />
        <div
          className={style.termsContent}
          dangerouslySetInnerHTML={{
            __html: isLoadingDocumentContent
              ? i18n.t("common:loading")
              : sanitizeHtml(selectedDocumentContent ?? ""),
          }}
        />
      </div>
    </Modal>
  );

  useEffect(() => {
    onAgreedChange?.(canAgree && agreed && allDocumentsAgreed);
  }, [agreed, allDocumentsAgreed, canAgree, onAgreedChange]);

  return (
    <div className={style.documents}>
      <div className="smallCopy strong dark">{title}</div>
      <Notification
        hideCloseButton
        open={documentsUnavailable}
        type="critical"
        text={
          isError
            ? i18n.t("common:termsFetchError")
            : i18n.t("common:termsNotAvailable")
        }
        data-testid="label-consent-documents-error"
      />
      {canAgree && (
        <ul className={style.documentsContent}>
          {documents.map((document) => (
            <li key={document.id} className={style.document}>
              <button
                type="button"
                className={Classnames("smallCopy primary", style.documentLink)}
                onClick={() => {
                  setSelectedDocumentId(document.id);
                }}
                data-testid={`button-consent-document-${document.id}`}
              >
                {i18n.t("common:terms", {
                  service: getServiceName(document.service),
                })}
              </button>
              {agreedDocumentIds.includes(document.id) && (
                <Chip
                  className={Classnames(style.documentStatus)}
                  borderColor="var(--primary-100)"
                  bgColor="var(--primary-50)"
                  textColor="var(--primary-600)"
                  shape="circle"
                  data-testid={`label-consent-document-${document.id}-agreed`}
                >
                  {i18n.t("common:agreed")}
                </Chip>
              )}
            </li>
          ))}
          {showPrivacyPolicy && (
            <li className={style.document}>
              <Link
                href="https://givery.co.jp/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                external
                className="smallCopy primary"
                onClick={() => markDocumentAsAgreed(PRIVACY_POLICY_ID)}
                data-testid={`button-consent-document-${PRIVACY_POLICY_ID}`}
              >
                {i18n.t("common:privacyPolicy")}
              </Link>
              {agreedDocumentIds.includes(PRIVACY_POLICY_ID) && (
                <Chip
                  className={Classnames(style.documentStatus)}
                  borderColor="var(--primary-100)"
                  bgColor="var(--primary-50)"
                  textColor="var(--primary-600)"
                  shape="circle"
                  data-testid={`label-consent-document-${PRIVACY_POLICY_ID}-agreed`}
                >
                  {i18n.t("common:agreed")}
                </Chip>
              )}
            </li>
          )}
        </ul>
      )}
      <div className={style.documentsSeparator} />
      <Checkbox
        disabled={!canAgree}
        value={canAgree && agreed}
        onChange={onAgreementChange}
        data-testid="checkbox-consent-documents"
      >
        <div className={style.checkboxLabel}>{agreementTitle}</div>
      </Checkbox>
      {renderConsentModal()}
    </div>
  );
};

export default ConsentDocuments;
